/**
 * aero_flight.ts — THE AERO LAW (2026-09-22): the sector flies like the sky.
 *
 * "Make the controls of your ship in space the same as flying in the atmosphere,
 * and add mechanics like you are flying on a planet." Both regimes always read
 * ONE stick (keybinds context `flight`: held throttle, mouse nose, A/D roll, R
 * level, Shift boost) and one integrator (velocity chases the nose — vehicle.md
 * §7.2 / §8). What differed: the sector scaled the MOUSE by the chassis
 * (`TURN × turnRate`: a Sovereign turned at 45 % of a Lancer's rate while the sky
 * turned every hull at `MOUSE_TURN` 0.0022 rad/px), banked only as a 0.45-rad
 * lean of the hull mesh, and knew no speed. This module is the pure half of the
 * fix, applied by `SpaceModule` under the `aero` model (the default):
 *
 *   · the sky's mouse constant for every hull — the chassis caps the TURN RATE
 *     instead (interceptors still dance, gunships still lumber, but a small
 *     stick motion feels identical on both sides of the cloud deck);
 *   · TURN AUTHORITY BY SPEED — full at the sweet spot (45 % of top speed: the
 *     X-Wing / Squadrons throttle sweet spot, Elite's blue zone), thinner at a
 *     standstill (thrusters, no airflow) and at overcruise (a bullet does not
 *     dogfight);
 *   · a COORDINATED BANK — the hull and the camera roll into a turn in
 *     proportion to the yaw rate and level out when it stops (the Rogue
 *     Squadron / Ace Combat "Normal" feel: the turn is flat, the picture banks);
 *   · G-BLEED — a hard turn shaves the commanded speed (Ace Combat's high-G
 *     turn), straight flight wins it back through the engine chase.
 *
 * References (2026-09-22 search): pseudo-atmospheric models give ships "predefined
 * pitch, roll and yaw rates" because players read aerial dogfights (Wikipedia,
 * "Space flight simulation game"); real craft cannot bank — "a banking turn
 * deflects off a cushion of air" (Baird, WTAMU) — and films/games bank anyway
 * for readability; Elite's Flight Assist "corrects the flight vector to match
 * the facing direction" with a blue-zone optimal speed; Squadrons/X-Wing put
 * peak turning at 50 % throttle; Ace Combat's High-G turns trade speed for
 * rate; Star Citizen argues the opposite (full Newtonian) and is the model this
 * one deliberately is not.
 */

export type FlightModel = 'aero' | 'space';
export const FLIGHT_MODEL_KEY = 'atlas:space-flight';

/** The player's sector flight model — `aero` unless they chose the old `space` model. */
export function flightModel(store: { getItem(k: string): string | null } | null = safeStorage()): FlightModel {
  try { return store?.getItem(FLIGHT_MODEL_KEY) === 'space' ? 'space' : 'aero'; } catch { return 'aero'; }
}
export function setFlightModel(m: FlightModel, store: { setItem(k: string, v: string): void } | null = safeStorage()): void {
  try { store?.setItem(FLIGHT_MODEL_KEY, m); } catch { /* private window */ }
}
function safeStorage(): Storage | null { try { return typeof localStorage === 'undefined' ? null : localStorage; } catch { return null; } }

export const AERO = {
  /** rad per pointer-locked mouse px — the SKY's constant (vehicle.md §5.3), chassis-blind. */
  MOUSE_TURN: 0.0022,
  /** rad/s the nose may turn at full authority on a turnRate-1 hull (a Lancer); × the chassis turnRate. */
  RATE_CAP: 2.4,
  /** The bank asked for per rad/s of yaw rate (s). */
  BANK_PER_RATE: 0.9,
  /** The deepest coordinated bank (rad, ≈ 60°). */
  BANK_MAX: 1.05,
  /** rad/s the picture rolls INTO a turn … */
  BANK_RATE: 3.2,
  /** … and back to level when the yaw stops (slower — a plane settles). */
  LEVEL_RATE: 1.6,
  /** The sweet spot, as a fraction of top speed: full turn authority here. */
  SWEET: 0.45,
  /** Authority at a standstill (thrusters alone) … */
  AUTH_SLOW: 0.7,
  /** … and at AUTH_FAST_AT × top speed and beyond (overcruise). */
  AUTH_FAST: 0.5,
  AUTH_FAST_AT: 3,
  /** A max-rate turn shaves this fraction off the commanded speed. */
  G_BLEED: 0.35,
  /** /s — the exponential smoothing of the tick rates into the felt rates. */
  RATE_SMOOTH: 8,
} as const;

/** Turn authority by speed: AUTH_SLOW at rest rising to 1 at the sweet spot, falling to AUTH_FAST by AUTH_FAST_AT × top. */
export function turnAuthority(speed: number, maxSpeed: number): number {
  const s = maxSpeed > 0 ? Math.max(0, speed / maxSpeed) : 0;
  if (s <= AERO.SWEET) return AERO.AUTH_SLOW + (1 - AERO.AUTH_SLOW) * (s / AERO.SWEET);
  const t = Math.min(1, (s - AERO.SWEET) / (AERO.AUTH_FAST_AT - AERO.SWEET));
  return 1 + (AERO.AUTH_FAST - 1) * t;
}

/** The look a tick may consume: the rest waits (a whip of the mouse is a request, the hull's rate is the answer). */
export function capTurn(delta: number, capPerTick: number): number {
  return Math.max(-capPerTick, Math.min(capPerTick, delta));
}

export interface AeroRates { yaw: number; pitch: number }

/** Smooth this tick's rates (rad/s) into the felt rates. */
export function smoothRates(r: AeroRates, yawRate: number, pitchRate: number, dt: number): void {
  const k = Math.min(1, dt * AERO.RATE_SMOOTH);
  r.yaw += (yawRate - r.yaw) * k;
  r.pitch += (pitchRate - r.pitch) * k;
}

/** The turn load, 0..1: how much of the hull's rate the felt rates use. */
export function turnLoad(r: AeroRates, capRate: number): number {
  if (capRate <= 0) return 0;
  return Math.min(1, (Math.abs(r.yaw) + Math.abs(r.pitch)) / capRate);
}

/** The coordinated bank a yaw rate asks for (+ = the left wing down for a left turn), clamped. */
export function bankTarget(yawRate: number): number {
  return Math.max(-AERO.BANK_MAX, Math.min(AERO.BANK_MAX, yawRate * AERO.BANK_PER_RATE));
}

/** One step of the picture's bank toward its target: BANK_RATE winding in, LEVEL_RATE unwinding. */
export function stepBank(bank: number, target: number, dt: number): number {
  const windingIn = Math.abs(target) > Math.abs(bank) && Math.sign(target) === (Math.sign(bank) || Math.sign(target));
  const rate = (windingIn ? AERO.BANK_RATE : AERO.LEVEL_RATE) * dt;
  const d = target - bank;
  return Math.abs(d) <= rate ? target : bank + Math.sign(d) * rate;
}

/** The commanded-speed scale under a turn load (G-bleed). */
export function commandedSpeedScale(load: number): number {
  return 1 - AERO.G_BLEED * Math.max(0, Math.min(1, load));
}
